import { I80Element, define, h } from "../base.js";
import { icon } from "../icons.js";
import { field, forward } from "../field.js";

/**
 * <i80-text-input label="Email" name="email" type="email" required helper-text="..." error="..."></i80-text-input>
 *
 * Renders a real <input> inside a labelled field. An <input> written inside the tag is
 * used as is, which is how server-rendered forms plug in:
 *   <i80-text-input label="Email" error="{{ form.email.errors.0 }}">{{ form.email }}</i80-text-input>
 *
 * type="password" gets a show/hide button (turn it off with no-visibility-toggle).
 */
export class I80TextInput extends I80Element {
  static get observedAttributes() {
    return ["label", "helper-text", "error", "invalid", "required", "optional", "disabled", "readonly", "width", "loading"];
  }

  setup() {
    let input = this.part("control") ?? this.querySelector("input");
    if (!input) {
      input = h("input", { type: this.getAttribute("type") || "text" });
      forward(this, input);
    }
    input.setAttribute("data-i80-part", "control");
    const wrapper = input.closest(".i80-form-text-input__wrapper") ?? h("div", { class: "i80-form-text-input__wrapper" }, input);
    this._input = input;
    this._wrapper = wrapper;
    this._password = input.type === "password" && !this.flag("no-visibility-toggle");
    if (this._password && !wrapper.querySelector(".i80-form-visibility-toggle")) {
      const toggle = h("button", { class: "i80-form-visibility-toggle i80-form-text-input__visibility-toggle", type: "button", "aria-controls": input.id || "" });
      toggle.addEventListener("click", () => {
        this._shown = !this._shown;
        input.type = this._shown ? "text" : "password";
        this._renderToggle();
      });
      wrapper.append(toggle);
    }
    this._render = field(this, input, { controlWrapper: wrapper });
    const toggle = wrapper.querySelector(".i80-form-visibility-toggle");
    if (toggle) toggle.setAttribute("aria-controls", input.id);
  }

  _renderToggle() {
    const toggle = this._wrapper.querySelector(".i80-form-visibility-toggle");
    if (!toggle) return;
    toggle.setAttribute("aria-label", this._shown ? "Hide password" : "Show password");
    toggle.replaceChildren(
      icon(this._shown ? "eye-off" : "eye"),
      h("span", { class: "sr-only", "aria-live": "polite" }, this._shown ? "Your password is shown" : "Your password is hidden"),
    );
  }

  update() {
    const input = this._input;
    if (this.hasAttribute("required")) input.required = this.flag("required");
    if (this.hasAttribute("disabled")) input.disabled = this.flag("disabled");
    if (this.hasAttribute("readonly")) input.readOnly = this.flag("readonly");
    const { invalid } = this._render();
    input.className = [
      "i80-form-text-input", "i80-typography-body-200", "i80-font-weight-regular",
      invalid && "i80-form-text-input--is-invalid",
      this._password && "i80-form-text-input--has-visibility-toggle",
      this.flag("loading") && "i80-form-text-input--is-loading",
    ].filter(Boolean).join(" ");
    this._wrapper.style.width = this.getAttribute("width") ?? "";
    this._renderToggle();
  }

  get value() {
    return this._input?.value ?? this.getAttribute("value") ?? "";
  }

  set value(v) {
    if (this._input) this._input.value = v;
    else this.setAttribute("value", v);
  }

  focus(options) {
    this._input?.focus(options);
  }
}

define("i80-text-input", I80TextInput);
